const FILTER_PARAM_PREFIX = "filter";

// Dispatched by whatever owns the applied filters (see facets.js) once the URL
// has settled, so links can re-read it. Also fired on history navigation.
const FILTERS_CHANGED_EVENT = "filters:changed";

class RelativeLink extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", this.onClick.bind(this));
  }

  connectedCallback() {
    const anchor = this.querySelector("a");
    if (!anchor) return;

    try {
      const url = new URL(anchor.href);
      anchor.href = url.pathname + url.search + url.hash;
    } catch (e) {
      // href is already relative or invalid — leave it as-is
    }

    this.syncActiveState();
  }

  /**
   * The `filter*` params this link carries, as [key, value] pairs. Empty unless
   * the link points at the current page, since filters can only be applied in
   * place there.
   */
  get filterParams() {
    const anchor = this.querySelector("a");
    if (!anchor) return [];

    let url;

    try {
      url = new URL(anchor.href, window.location.href);
    } catch (e) {
      return [];
    }

    if (url.pathname !== window.location.pathname) return [];

    const filterParams = [];

    url.searchParams.forEach((value, key) => {
      if (key.startsWith(FILTER_PARAM_PREFIX)) filterParams.push([key, value]);
    });

    return filterParams;
  }

  /** Whether every filter this link carries is currently applied to the page. */
  get isActive() {
    const filterParams = this.filterParams;
    if (!filterParams.length) return false;

    const applied = new URLSearchParams(window.location.search);

    return filterParams.every(([key, value]) =>
      applied.getAll(key).includes(value),
    );
  }

  /**
   * Reflects whether this link's filters are applied, for sections to style an
   * active link off `relative-link[data-active]`.
   *
   * State is always derived from the current URL rather than toggled on click,
   * because the same filter can be removed from anywhere else on the page —
   * a facet checkbox, an active-filter chip, or the back button.
   */
  syncActiveState() {
    const active = this.isActive;
    const anchor = this.querySelector("a");

    this.toggleAttribute("data-active", active);

    if (!anchor) return;

    if (active) {
      anchor.setAttribute("aria-current", "true");
    } else {
      anchor.removeAttribute("aria-current");
    }
  }

  /**
   * Offers the `filter*` params of links pointing at the current page to the
   * page itself, so filters can be applied — or, for a link already showing as
   * active, taken back off — without a page load. How they merge with the
   * filters already applied is up to the listener (see facets.js).
   *
   * The click is only swallowed if a listener cancels the dispatched event to
   * claim it, so links still navigate normally on pages without filtering.
   */
  onClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const anchor = event.target.closest("a");
    if (!anchor || !this.contains(anchor)) return;
    if (anchor.target && anchor.target !== "_self") return;

    const filterParams = this.filterParams;
    if (!filterParams.length) return;

    const action = this.isActive ? "remove-filters" : "add-filters";

    const filterEvent = new CustomEvent(`relative-link:${action}`, {
      cancelable: true,
      detail: { filterParams },
    });

    document.dispatchEvent(filterEvent);

    if (filterEvent.defaultPrevented) event.preventDefault();
  }
}

const syncActiveStates = () => {
  document
    .querySelectorAll("relative-link")
    .forEach((link) => link.syncActiveState());
};

if (!customElements.get("relative-link")) {
  customElements.define("relative-link", RelativeLink);

  document.addEventListener(FILTERS_CHANGED_EVENT, syncActiveStates);
  window.addEventListener("popstate", syncActiveStates);
}
